import { useEffect, useState } from "react";
import { api, resolveAssetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";

const CATS = ["Bank", "NBFC", "Fintech"];

export default function AdminPartners() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Bank", logo_url: "", description: "" });

  const load = async () => { const { data } = await api.get("/partners"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name:"", category:"Bank", logo_url:"", description:"" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) await api.patch(`/admin/partners/${editing.id}`, form);
      else await api.post("/admin/partners", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await api.delete(`/admin/partners/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div><div className="overline">Network</div><h1 className="font-display text-3xl font-extrabold mt-1">Partners</h1></div>
        <Button data-testid="partner-add" className="btn-saffron rounded-full" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>Add partner</Button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map(p => (
          <div key={p.id} className="card-flat p-5 relative">
            <div className="absolute top-3 right-3 flex gap-1">
              <button data-testid={`partner-edit-${p.id}`} onClick={()=>openEdit(p)} className="text-ink-muted hover:text-saffron"><Pencil className="h-4 w-4"/></button>
              <button data-testid={`partner-delete-${p.id}`} onClick={()=>remove(p.id)} className="text-ink-muted hover:text-red-500"><Trash2 className="h-4 w-4"/></button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-saffron">{p.category}</div>
            {p.logo_url ? (
              <img src={resolveAssetUrl(p.logo_url)} alt={p.name} className="h-12 w-auto mt-2 object-contain"/>
            ) : (
              <div className="font-display font-bold text-lg mt-1">{p.name}</div>
            )}
            {p.logo_url && <div className="text-xs text-ink-muted mt-1">{p.name}</div>}
            <div className="text-xs text-ink-muted mt-2">{p.description}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} partner</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input data-testid="partner-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger data-testid="partner-form-category"><SelectValue/></SelectTrigger>
                <SelectContent>{CATS.map(c=> <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <ImageUploader testid="partner-form-logo" label="Logo" value={form.logo_url} onChange={(v)=>setForm({...form, logo_url:v})}/>
            <Field label="Description"><Input data-testid="partner-form-desc" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button className="btn-saffron rounded-full" data-testid="partner-form-save" onClick={save}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
